import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiDownload, FiArrowLeft } from 'react-icons/fi';
import { CheckCircle2, AlertTriangle, Clock3, FileText } from 'lucide-react';
import {
  COMPANY_DOCUMENTS,
  COMPANY_DOCUMENT_MAP,
} from '../../../../constants/companyDocuments';
import { getCompanyPortalPath } from '../../../../constants/companyRoutes';
import {
  COMPANY_DOCUMENT_STATUS,
  getCompanyDocumentFileUrl,
  listCompanyDocuments,
  mapDocumentsByKey,
  uploadCompanyDocument,
} from '../../../../services/companyDocuments';
import './VerificationTab.css';

const STATUS_META = {
  [COMPANY_DOCUMENT_STATUS.NONE]: {
    label: 'Sin cargar',
    description: 'Todavia no se carga evidencia para este requisito.',
  },
  [COMPANY_DOCUMENT_STATUS.PENDING]: {
    label: 'En revision',
    description: 'Estamos validando el archivo enviado.',
  },
  [COMPANY_DOCUMENT_STATUS.VERIFIED]: {
    label: 'Verificado',
    description: 'Documento aprobado por el equipo de Flotte.',
  },
  [COMPANY_DOCUMENT_STATUS.REJECTED]: {
    label: 'Requiere atencion',
    description: 'Sube un nuevo archivo para continuar con la validacion.',
  },
};

export default function VerificationTab({
  companyId,
  canUpload = true,
  showBackLink = true,
}) {
  const [documents, setDocuments] = useState([]);
  const [uploadingByKey, setUploadingByKey] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const documentsByCategory = useMemo(() => {
    const categoryMap = new Map();

    (COMPANY_DOCUMENTS || []).forEach(document => {
      if (!categoryMap.has(document.category)) {
        categoryMap.set(document.category, []);
      }
      categoryMap.get(document.category).push(document);
    });

    return Array.from(categoryMap, ([category, items]) => ({
      category,
      documents: items,
    }));
  }, []);

  const [activeCategory, setActiveCategory] = useState(() =>
    documentsByCategory.length > 0 ? documentsByCategory[0].category : ''
  );

  useEffect(() => {
    if (!documentsByCategory.some(section => section.category === activeCategory)) {
      const fallback = documentsByCategory[0]?.category || '';
      setActiveCategory(fallback);
    }
  }, [documentsByCategory, activeCategory]);

  const documentsByKey = useMemo(() => mapDocumentsByKey(documents), [documents]);

  const refreshDocuments = useCallback(async () => {
    if (!companyId) {
      setDocuments([]);
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      const list = await listCompanyDocuments(companyId);
      setDocuments(list);
    } catch (error) {
      setLoadError(error?.message || 'No se pudo cargar la informacion de verificacion.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    refreshDocuments();
  }, [refreshDocuments]);

  const tabOptions = useMemo(() => {
    if (documentsByCategory.length === 0) {
      return [];
    }
    return documentsByCategory.map(section => ({
      id: section.category,
      label: section.category,
    }));
  }, [documentsByCategory]);

  const currentSection = useMemo(() => {
    if (documentsByCategory.length === 0) {
      return null;
    }
    return (
      documentsByCategory.find(section => section.category === activeCategory) ||
      documentsByCategory[0]
    );
  }, [activeCategory, documentsByCategory]);

  const categorySummary = useMemo(() => {
    if (!currentSection) {
      return { verified: 0, inProgress: 0, rejected: 0 };
    }
    return currentSection.documents.reduce(
      (accumulator, document) => {
        const record = documentsByKey[document.key];
        const status = record?.status || COMPANY_DOCUMENT_STATUS.NONE;
        if (status === COMPANY_DOCUMENT_STATUS.VERIFIED) {
          accumulator.verified += 1;
        }
        if (status === COMPANY_DOCUMENT_STATUS.PENDING) {
          accumulator.inProgress += 1;
        }
        if (status === COMPANY_DOCUMENT_STATUS.REJECTED) {
          accumulator.rejected += 1;
        }
        return accumulator;
      },
      { verified: 0, inProgress: 0, rejected: 0 }
    );
  }, [currentSection, documentsByKey]);

  const categoryDocuments = currentSection?.documents || [];
  const totalDocuments = categoryDocuments.length;

  let badgeLabel = '';
  if (totalDocuments === 0) {
    badgeLabel = 'Sin requisitos configurados';
  } else if (categorySummary.verified === totalDocuments) {
    badgeLabel = 'Todo verificado';
  } else if (categorySummary.verified === 0) {
    badgeLabel = `${totalDocuments} requisitos pendientes`;
  } else {
    badgeLabel = `${categorySummary.verified}/${totalDocuments} verificados`;
  }

  const extraBadges = [];
  if (categorySummary.inProgress > 0) {
    extraBadges.push(`${categorySummary.inProgress} en revision`);
  }
  if (categorySummary.rejected > 0) {
    extraBadges.push(`${categorySummary.rejected} rechazados`);
  }
  if (extraBadges.length > 0 && totalDocuments > 0) {
    badgeLabel = `${badgeLabel} - ${extraBadges.join(' - ')}`;
  }

  const progressPercent = totalDocuments
    ? Math.round((categorySummary.verified / totalDocuments) * 100)
    : 0;
  const clampedProgress = Math.min(100, Math.max(0, progressPercent));

  const statusIcons = useMemo(
    () => ({
      [COMPANY_DOCUMENT_STATUS.VERIFIED]: <CheckCircle2 aria-hidden="true" />,
      [COMPANY_DOCUMENT_STATUS.REJECTED]: <AlertTriangle aria-hidden="true" />,
      [COMPANY_DOCUMENT_STATUS.PENDING]: <Clock3 aria-hidden="true" />,
      [COMPANY_DOCUMENT_STATUS.NONE]: <FileText aria-hidden="true" />,
    }),
    []
  );

  const statusPillClass = {
    [COMPANY_DOCUMENT_STATUS.VERIFIED]: 'verification-status-pill verification-status-pill--success',
    [COMPANY_DOCUMENT_STATUS.PENDING]: 'verification-status-pill verification-status-pill--warning',
    [COMPANY_DOCUMENT_STATUS.REJECTED]: 'verification-status-pill verification-status-pill--danger',
    [COMPANY_DOCUMENT_STATUS.NONE]: 'verification-status-pill verification-status-pill--neutral',
  };

  const uploadDocumentInstantly = useCallback(
    async (docKey, file) => {
      try {
        const definition = COMPANY_DOCUMENT_MAP[docKey];
        const title = definition?.label || docKey;
        await uploadCompanyDocument({
          companyId,
          documentKey: docKey,
          file,
          title,
        });
        setSuccessMessage(
          `"${title}" enviado correctamente. El equipo de Flotte revisara cada archivo.`
        );
        await refreshDocuments();
      } catch (error) {
        setSubmitError(error?.message || 'No se pudieron cargar los documentos.');
      } finally {
        setUploadingByKey(previous => {
          const next = { ...previous };
          delete next[docKey];
          return next;
        });
      }
    },
    [companyId, refreshDocuments]
  );

  const handleFileChange = async (event, docKey) => {
    setSubmitError('');
    setSuccessMessage('');

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Allow reselecting the same file by clearing the input value.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';

    if (!companyId) {
      setSubmitError('No encontramos una empresa vinculada a tu cuenta.');
      return;
    }

    if (!canUpload) {
      setSubmitError('No tienes permisos para subir documentos.');
      return;
    }

    setUploadingByKey(previous => ({
      ...previous,
      [docKey]: {
        name: file.name,
      },
    }));

    await uploadDocumentInstantly(docKey, file);
  };

  const showEmptyCategories = !currentSection && !loading;

  return (
    <section className="verification-form">
      {loadError && <div className="verification-alert error">{loadError}</div>}
      {submitError && <div className="verification-alert error">{submitError}</div>}
      {successMessage && (
        <div className="verification-alert success">{successMessage}</div>
      )}
      {!canUpload && (
        <div className="verification-alert">
          Tu cuenta tiene acceso de solo lectura. Contacta al equipo de Flotte si necesitas
          actualizar la documentacion.
        </div>
      )}

      <div className="verification-panel">
        <aside className="verification-panel__nav">
          {showBackLink ? (
            <Link to={getCompanyPortalPath()} className="verification-nav-back">
              <FiArrowLeft aria-hidden="true" />
              <span>Volver al perfil de empresa</span>
            </Link>
          ) : null}
          <p className="verification-nav-label">Tipos de documento</p>
          {tabOptions.length > 0 ? (
            <nav className="verification-nav" aria-label="Tipos de documentos">
              {tabOptions.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  className={`verification-nav-item${activeCategory === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveCategory(tab.id)}
                  aria-pressed={activeCategory === tab.id}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          ) : (
            <p className="verification-nav-empty">No hay categorias disponibles.</p>
          )}
        </aside>

        <div className="verification-panel__content">
          {loading && <p className="verification-loading">Cargando documentos...</p>}

          {currentSection && (
            <>
              <header className="verification-content-header">
                <div>
                  <p className="verification-content-eyebrow">
                    Documentos de tipo {currentSection.category.toLowerCase()}.
                  </p>
                  <h2>{currentSection.category}</h2>
                </div>
                <div className="verification-progress">
                  <span className="verification-progress__label">{badgeLabel}</span>
                  <div className="verification-progress__track" role="presentation">
                    <span
                      className="verification-progress__fill"
                      style={{ width: `${clampedProgress}%` }}
                    />
                  </div>
                </div>
              </header>

              {categoryDocuments.length === 0 ? (
                <p className="verification-empty">No hay documentos para esta categoria.</p>
              ) : (
                <div className="verification-doc-list" role="list">
                  {categoryDocuments.map(document => {
                    const record = documentsByKey[document.key];
                    const status = record?.status || COMPANY_DOCUMENT_STATUS.NONE;
                    const metadata =
                      STATUS_META[status] || STATUS_META[COMPANY_DOCUMENT_STATUS.NONE];
                    const pendingUpload = uploadingByKey[document.key];
                    const localFileName = pendingUpload?.name;
                    const isUploading = Boolean(pendingUpload);
                    const inputId = `upload-${document.key}`;
                    const hasRemoteFile = Boolean(record?.file);
                    const remoteUrl = hasRemoteFile ? getCompanyDocumentFileUrl(record) : '';
                    const remoteFileTitle = record?.title || document.label;
                    const isVerified = status === COMPANY_DOCUMENT_STATUS.VERIFIED;
                    const uploadDisabled = !canUpload || isVerified || isUploading;
                    const baseActionLabel = hasRemoteFile ? 'Reemplazar' : 'Subir';
                    let uploadLabel = baseActionLabel;
                    if (isUploading) {
                      uploadLabel = 'Subiendo...';
                    } else if (isVerified) {
                      uploadLabel = 'Verificado';
                    }
                    const updatedAt = record?.reviewedAt || record?.updated;
                    const updatedLabel = updatedAt
                      ? new Date(updatedAt).toLocaleString()
                      : 'Sin registro';

                    return (
                      <article key={document.key} className="verification-doc-row" role="listitem">
                        <div className="verification-doc-info">
                          <span
                            className={`verification-doc-icon status-${status}`}
                            title={metadata.label}
                            aria-label={metadata.label}
                          >
                            <FileText aria-hidden="true" className="verification-doc-icon__file" />
                            <span className="verification-doc-icon__badge">
                              {statusIcons[status] || statusIcons[COMPANY_DOCUMENT_STATUS.NONE]}
                            </span>
                          </span>
                          <div className="verification-doc-info__body">
                            <div className="verification-doc-heading">
                              <p className="verification-doc-name" title={document.label}>
                                {document.label}
                              </p>
                              <span
                                className={
                                  statusPillClass[status] ||
                                  statusPillClass[COMPANY_DOCUMENT_STATUS.NONE]
                                }
                              >
                                {metadata.label}
                              </span>
                            </div>
                            <p className="verification-doc-meta">
                              {document.required ? 'Obligatorio' : 'Opcional'}
                            </p>
                            <p className="verification-doc-date">
                              Última actualización: {updatedLabel}
                            </p>
                          </div>
                        </div>

                        <div className="verification-doc-actions">
                          {record?.notes && status === COMPANY_DOCUMENT_STATUS.REJECTED ? (
                            <span className="verification-doc-feedback">
                              Rechazado: {record.notes}
                            </span>
                          ) : null}
                          {localFileName ? (
                            <span className="verification-doc-file uploading">
                              Archivo local: {localFileName}
                            </span>
                          ) : null}
                          <div className="verification-doc-links">
                            {hasRemoteFile ? (
                              <a
                                href={remoteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={remoteFileTitle}
                                className="verification-doc-link"
                                aria-label={`Ver ${remoteFileTitle}`}
                              >
                                <FiDownload aria-hidden="true" />
                                <span>Ver detalles</span>
                              </a>
                            ) : (
                              <span className="verification-doc-placeholder">Sin archivo</span>
                            )}

                            <label
                              className={`verification-doc-link verification-doc-link--action${
                                uploadDisabled ? ' disabled' : ''
                              }`}
                              htmlFor={inputId}
                            >
                              <input
                                id={inputId}
                                type="file"
                                className="verification-upload-input"
                                onChange={event => handleFileChange(event, document.key)}
                                accept={document.accept || undefined}
                                disabled={uploadDisabled}
                              />
                              <span>{uploadLabel}</span>
                            </label>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {showEmptyCategories && (
            <p className="verification-empty">No hay requisitos configurados todavia.</p>
          )}
        </div>
      </div>

      <button
        type="button"
        className="verification-footer-submit"
        onClick={refreshDocuments}
        disabled={loading}
      >
        {loading ? 'Guardando...' : 'Guardar cambios'}
      </button>
    </section>
  );
}
